/*  MUI  */
import { Skeleton, Typography } from '@mui/material';

/*  React  */
import React, { useMemo } from 'react';
import { Link } from 'react-router';

/*  Components  */
import { DataCard } from './Components/DataCard';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const calculate = (sum: any): number => {
    let currentSum = sum;
    if (Array.isArray(currentSum)) {
        const elements = currentSum;
        currentSum = 0;
        elements.forEach((entry) => {
            const { value } = entry;
            currentSum += parseInt(value, 10);
        });
    }

    return currentSum;
};

const SumCard = ({
    children,
    height,
    link,
    title
}: {
    children: JSX.Element;
    height?: string;
    link?: string;
    title?: string;
}) => {
    if (!link) {
        return (
            <DataCard minHeight={height} title={title}>
                {children}
            </DataCard>
        );
    }

    return (
        <Link to={link} className="link" data-trackid="cards_sum_routeTo">
            <DataCard minHeight={height} title={title}>
                {children}
            </DataCard>
        </Link>
    );
};

const SumText = ({
    color,
    loading,
    sum
}: {
    color: string;
    loading: boolean;
    sum?: number;
}) => (
    <Typography variant="h1" sx={{ color }}>
        {loading ? <Skeleton animation="wave" /> : sum}
    </Typography>
);

/**
 * Component that displays a sum value either as a card or standalone number
 * @param {Object} props - Component properties
 * @param {string} [props.color] - Color of the sum text
 * @param {string} [props.height] - Minimum height of the card
 * @param {string} [props.link] - URL to navigate to when clicking the card
 * @param {boolean} props.loading - Whether to show loading skeleton
 * @param {boolean} [props.noCard] - Whether to render without card wrapper
 * @param {number} props.sum - The sum value to display
 * @param {string} [props.title] - Title text for the card
 * @returns {JSX.Element} Rendered sum component
 */
export const Sum = ({
    color,
    height,
    link,
    loading,
    noCard,
    sum: propsSum,
    title
}: {
    color?: string;
    height?: string;
    link?: string;
    loading: boolean;
    noCard?: boolean;
    sum: number;
    title?: string;
}): JSX.Element => {
    const sum = useMemo(() => calculate(propsSum), [propsSum]);
    const currentColor = color || 'text.cardKPI';

    if (noCard) {
        return <SumText color={currentColor} loading={loading} sum={sum} />;
    }

    return (
        <SumCard height={height} link={link} title={title}>
            <SumText color={currentColor} loading={loading} sum={sum} />
        </SumCard>
    );
};
